<script lang="ts">
	import { graphql, type DashboardReportShareType$options } from '$houdini'
	import {
		DashboardConfiguration,
		ManageConfiguration,
		type ClientChartConfiguration,
		type ReportInput,
		type Parameter,
		type ChartReportMembership,
	} from '@isoftdata/svelte-dashboard'
	import { translate } from '@isoftdata/utility-string'
	import {
		loadChartDataForReport,
		loadDashboardReportMetadata,
		formatReportOutput,
		isNonNullable,
	} from 'utility/dashboard-helper'
	import session from 'stores/session'
	import type { Mediator, SvelteAsr } from 'types/common'

	import { getContext, type ComponentProps } from 'svelte'
	import type { Chart } from '@isoftdata/utility-dashboard-backend'

	type ManageProps = ComponentProps<typeof ManageConfiguration>

	interface Props {
		asr: SvelteAsr
		dashboardReportId: number | null
		groups: ManageProps['groups']
		plants: ManageProps['sites']
		users: ManageProps['users']
		reports: ManageProps['reports']
		canEditOwn: boolean
		canEditAll: boolean
		charts: Array<Chart>
		defaultParameters: Array<Parameter>
	}

	let {
		//
		asr,
		dashboardReportId = $bindable(),
		groups,
		users,
		plants: sites,
		reports,
		canEditOwn,
		canEditAll,
		defaultParameters,
	}: Props = $props()

	const mediator = getContext<Mediator>('mediator')

	function showError(heading: string, message: string) {
		mediator.publish('showMessage', {
			heading,
			message,
			color: 'danger',
			time: Infinity,
		})
	}

	type SaveDashboardReportInput = Parameters<ComponentProps<typeof DashboardConfiguration>['saveDashboardReport']>[0]

	async function saveDashboardReport({ dashboardReport }: SaveDashboardReportInput): Promise<ReportInput> {
		let savedReport: ReportInput

		try {
			if (dashboardReport.dashboardReportId) {
				const { data } = await updateReport.mutate({
					input: {
						id: dashboardReport.dashboardReportId,
						name: dashboardReport.reportName,
						title: dashboardReport.reportTitle,
						json: JSON.stringify({
							parameters: dashboardReport.parameters,
							autoRefreshInterval: dashboardReport.autoRefreshInterval,
						}),
						shareId: dashboardReport.shareId,
						shareType: clientShareTypeToApiType(dashboardReport.shareType),
					},
				})

				if (!data?.updateDashboardReport) {
					throw new Error(translate('common:noDataReturnErrorMessage', 'No data was returned from the server'))
				}

				savedReport = formatReportOutput(data.updateDashboardReport)
			} else {
				const { data } = await createReport.mutate({
					input: {
						name: dashboardReport.reportName,
						title: dashboardReport.reportTitle,
						json: JSON.stringify({
							parameters: dashboardReport.parameters,
							autoRefreshInterval: dashboardReport.autoRefreshInterval,
						}),
						shareId: dashboardReport.shareId,
						shareType: clientShareTypeToApiType(dashboardReport.shareType),
					},
				})

				if (!data?.createDashboardReport) {
					throw new Error(translate('common:noDataReturnErrorMessage', 'No data was returned from the server'))
				}

				savedReport = formatReportOutput(data.createDashboardReport)
			}
		} catch (err: any) {
			showError(translate('configuration.dashboard.saveReportError', 'Error Saving Report'), err.message)
			throw err
		}

		return savedReport
	}

	function clientShareTypeToApiType(shareType: ReportInput['shareType']): DashboardReportShareType$options {
		return shareType.toUpperCase() as DashboardReportShareType$options
	}

	// #region GraphQL Documents
	const membershipQuery = graphql(`
		query DashboardReportChartMembership($reportId: Int!) {
			dashboardChartMembership(reportId: $reportId) {
				chartId: id
				rank
				title
				supertype
				inReport
				chartType: type
				showChart: display
			}
		}
	`)
	const createReport = graphql(`
		mutation CreateDashboardReport($input: CreateDashboardReportInput!) {
			createDashboardReport(input: $input) {
				dashboardReportId: id
				reportName: name
				reportTitle: title
				json
				shareType
				shareId
				ownerId
			}
		}
	`)

	const updateReport = graphql(`
		mutation UpdateDashboardReport($input: UpdateDashboardReportInput!) {
			updateDashboardReport(input: $input) {
				dashboardReportId: id
				reportName: name
				reportTitle: title
				json
				shareType
				shareId
				ownerId
			}
		}
	`)

	const deleteReport = graphql(`
		mutation DeleteDashboardReport($deleteDashboardReportId: Int!) {
			deleteDashboardReport(id: $deleteDashboardReportId) {
				id
				message
				success
			}
		}
	`)

	const updateReportChartOptions = graphql(`
		mutation UpdateDashboardChartOptions($input: UpdateDashboardReportInput!) {
			updateDashboardReport(input: $input) {
				chartMembership {
					chartId: id
					title
					showChart: display
					inReport
					supertype
					chartType: type
				}
				chartEntries {
					id
					rank
					chart {
						id
					}
				}
			}
		}
	`)

	const addChartToReportMutation = graphql(`
		mutation AddChartToReportMutation($input: AddChartToDashboardReportInput!) {
			addChartToDashboardReport(input: $input) {
				chartId: id
				showChart: display
				inReport
				rank
				supertype
				title
				chartType: type
			}
		}
	`)

	const removeChartFromReportMutation = graphql(`
		mutation RemoveChartFromReportMutation($reportId: Int!, $chartId: Int!) {
			removeChartFromDashboardReport(reportId: $reportId, chartId: $chartId) {
				chartId: id
				showChart: display
				inReport
				rank
				supertype
				title
				chartType: type
			}
		}
	`)
	// #endregion
</script>

<DashboardConfiguration
	{reports}
	{defaultParameters}
	currentUserAccountId={$session.userAccountId}
	bind:selectedReportId={dashboardReportId}
	disableConfigureReport={!canEditOwn && !canEditAll}
	{saveDashboardReport}
	deleteDashboardReport={async ({ dashboardReportId }) => {
		try {
			await deleteReport.mutate({
				deleteDashboardReportId: dashboardReportId,
			})
		} catch (err: any) {
			showError(translate('configuration.dashboard.deleteReportError', 'Error Deleting Report'), err.message)
		}
	}}
	openReportClick={dashboardReportId => asr.go('app.dashboard', { dashboardReportId })}
	selectedReportChange={dashboardReportId => {
		dashboardReportId ??= null
		asr.go(null, { dashboardReportId }, { inherit: true })
	}}
></DashboardConfiguration>

{#if dashboardReportId}
	<ManageConfiguration
		additionalSessionParameters={['token']}
		currentStoreId={$session.currentStore}
		currentUserAccountId={$session.userAccountId}
		selectedReportId={dashboardReportId}
		siteLabel={translate('common:plant', 'Plant')}
		{groups}
		{users}
		{sites}
		{reports}
		{loadChartDataForReport}
		{loadDashboardReportMetadata}
		{saveDashboardReport}
		loadDashboardChartMembershipForReport={async ({ dashboardReportId }): Promise<Array<ChartReportMembership>> => {
			try {
				const { data } = await membershipQuery.fetch({
					variables: {
						reportId: dashboardReportId,
					},
				})

				return (
					data?.dashboardChartMembership
						?.filter(c => c.showChart)
						?.map(c => ({
							...c,
							dashboardReportId,
							inReport: !!c.inReport,
							supertype: c.supertype.toLowerCase() as 'embed' | 'table' | 'google',
							chartType: c.chartType as ClientChartConfiguration['chartWrapper']['chartType'] | 'embed' | 'table',
						})) ?? []
				)
			} catch (err: any) {
				showError(
					translate('configuration.dashboard.loadChartMembershipError', 'Error Loading Chart Membership'),
					err.message,
				)
				throw err
			}
		}}
		updateDashboardChartMembershipForReport={async (membership, originalMembership) => {
			if (membership.inReport && !originalMembership.inReport) {
				const { data } = await addChartToReportMutation.mutate({
					input: {
						chartId: membership.chartId,
						reportId: membership.dashboardReportId,
						rank: membership.rank ?? undefined,
					},
				})

				return (
					data?.addChartToDashboardReport
						?.filter(chart => chart?.showChart && isNonNullable(chart))
						.map((chart): ChartReportMembership => {
							return {
								...chart!,
								title: chart?.title ?? '',
								inReport: !!chart?.inReport,
								chartType: chart?.chartType as ChartReportMembership['chartType'],
								supertype: (chart?.supertype ?? 'google').toLowerCase() as ChartReportMembership['supertype'],
								dashboardReportId: membership.dashboardReportId,
							}
						}) ?? []
				)
			} else if (membership.inReport && originalMembership.inReport) {
				const { data } = await updateReportChartOptions.mutate({
					input: {
						id: membership.dashboardReportId,
						charts: [
							{
								chartId: membership.chartId,
								rank: membership.rank,
							},
						],
					},
				})
				return (
					data?.updateDashboardReport?.chartMembership
						.filter(chart => chart.showChart)
						.map((chart): ChartReportMembership => {
							const entry = data?.updateDashboardReport?.chartEntries.find(chartEntry => {
								return chartEntry?.chart?.id === chart.chartId
							})
							return {
								...chart,
								inReport: !!chart.inReport,
								chartType: chart.chartType as ChartReportMembership['chartType'],
								supertype: (chart.supertype ?? 'google').toLowerCase() as ChartReportMembership['supertype'],
								rank: entry && entry?.rank !== null && entry.rank >= 0 ? entry.rank : null,
								dashboardReportId: membership.dashboardReportId,
							}
						}) ?? []
				)
			} else {
				const { data } = await removeChartFromReportMutation.mutate({
					chartId: membership.chartId,
					reportId: membership.dashboardReportId,
				})

				return (
					data?.removeChartFromDashboardReport
						.filter(chart => chart.showChart)
						.map((chart): ChartReportMembership => {
							return {
								...chart,
								inReport: !!chart.inReport,
								chartType: chart.chartType as ChartReportMembership['chartType'],
								supertype: chart.supertype.toLowerCase() as ChartReportMembership['supertype'],
								dashboardReportId: membership.dashboardReportId,
							}
						}) ?? []
				)
			}
		}}
		saveDashboardReportChartOptions={async ({ dashboardReportChart }) => {
			try {
				await updateReportChartOptions.mutate({
					input: {
						id: dashboardReportChart.dashboardReportId,
						charts: [
							{
								chartId: dashboardReportChart.chartId,
								jsonOverride: dashboardReportChart.jsonOverride
									? JSON.stringify(dashboardReportChart.jsonOverride)
									: null,
							},
						],
					},
				})
			} catch (err: any) {
				showError(translate('configuration.dashboard.saveChartOptionsError', 'Error Saving Chart Options'), err.message)
			}
		}}
	></ManageConfiguration>
{:else}
	{translate('dashboard.noReportSelected', 'No Report Selected')}
{/if}
